import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { auditService } from 'src/data/api/helios-service/auditService';
import { ApiRequestStatus, initApiStatus } from 'src/types/ApiRequestStatus';
import {
  Audit,
  CreateAuditRequest,
  GetAuditStatsRequest,
  GetAuditStatsResponse,
  GetAuditSummaryRequest,
  GetAuditSummaryResponse,
  ListAuditsRequest,
  SearchAuditsRequest,
  UpdateAuditRequest,
  ReopenAuditRequest,
  GetAuditRequest,
} from 'src/types/helios/Audit';
import { handleError } from 'src/utils/generalUtils';

export interface UpdateAuditApiStatus extends ApiRequestStatus {
  updatedAudit?: Audit;
}
export interface AuditState {
  auditsList: Audit[];
  currentAudit?: Audit;
  auditSummary?: GetAuditSummaryResponse;
  auditStats?: GetAuditStatsResponse;
  fetchAuditsApiStatus: ApiRequestStatus;
  createAuditApiStatus: ApiRequestStatus;
  updateAuditApiStatus: UpdateAuditApiStatus;
  reopenAuditApiStatus: ApiRequestStatus;
  searchAuditsApiStatus: ApiRequestStatus;
  auditSummaryApiStatus: ApiRequestStatus;
  auditStatsApiStatus: ApiRequestStatus;
  getAuditByIdApiStatus?: ApiRequestStatus;
}

const initialState: AuditState = {
  auditsList: [],
  fetchAuditsApiStatus: initApiStatus,
  createAuditApiStatus: initApiStatus,
  updateAuditApiStatus: initApiStatus,
  reopenAuditApiStatus: initApiStatus,
  searchAuditsApiStatus: initApiStatus,
  auditSummaryApiStatus: initApiStatus,
  auditStatsApiStatus: initApiStatus,
  getAuditByIdApiStatus: initApiStatus,
};

export const fetchAuditsApiThunk = createAsyncThunk(
  'audit/fetchAudits',
  async (data: ListAuditsRequest, { rejectWithValue }) => {
    try {
      const response = await auditService.getAll(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const getAuditByIdApiThunk = createAsyncThunk(
  'audit/getAuditsApiThunk',
  async (data: GetAuditRequest, { rejectWithValue }) => {
    try {
      const response = await auditService.getById(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const createAuditApiThunk = createAsyncThunk(
  'audit/createAudit',
  async (data: CreateAuditRequest, { rejectWithValue }) => {
    try {
      const response = await auditService.create(data);
      return {
        audit: response.audit,
        message: 'Audit created successfully',
      };
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const updateAuditApiThunk = createAsyncThunk(
  'audit/updateAudit',
  async (data: UpdateAuditRequest, { rejectWithValue }) => {
    try {
      const response = await auditService.update(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const reopenAuditApiThunk = createAsyncThunk(
  'audit/reopenAudit',
  async (data: ReopenAuditRequest, { rejectWithValue }) => {
    try {
      const response = await auditService.reopen(data);
      return {
        audit: response.audit,
        message: 'Audit reopened successfully',
      };
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const searchAuditsApiThunk = createAsyncThunk(
  'audit/searchAudits',
  async (data: SearchAuditsRequest, { rejectWithValue }) => {
    try {
      const response = await auditService.search(data);

      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const getAuditSummaryApiThunk = createAsyncThunk(
  'audit/getAuditSummary',
  async (data: GetAuditSummaryRequest, { rejectWithValue }) => {
    try {
      const response = await auditService.getSummary(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const getAuditStatsApiThunk = createAsyncThunk(
  'audit/getAuditStats',
  async (data: GetAuditStatsRequest, { rejectWithValue }) => {
    try {
      const response = await auditService.getStats(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

const auditsSlice = createSlice({
  name: 'audits',
  initialState,
  reducers: {
    resetAllApiStatus: (state) => {
      state.fetchAuditsApiStatus = initApiStatus;
      state.createAuditApiStatus = initApiStatus;
      state.updateAuditApiStatus = initApiStatus;
      state.reopenAuditApiStatus = initApiStatus;
      state.searchAuditsApiStatus = initApiStatus;
      state.auditSummaryApiStatus = initApiStatus;
      state.auditStatsApiStatus = initApiStatus;
    },
    addAuditToState: (state, action: PayloadAction<Audit>) => {
      state.auditsList.push(action.payload);
    },
    updateAuditInState: (state, action: PayloadAction<Audit>) => {
      const index = state.auditsList.findIndex(
        (audit) => audit.auditId === action.payload.auditId,
      );
      if (index !== -1) {
        state.auditsList[index] = {
          ...action.payload,
          lastUpdatedDate: action.payload.lastUpdatedAt,
          createdDate: action.payload.createdAt,
          businessUnit: action.payload.businessUnitId || action.payload.businessUnit,
        };
      }
    },
    clearAPIStatusMessages: (state) => {
      state.createAuditApiStatus.errorMessage = '';
      state.createAuditApiStatus.successMessage = '';
      state.updateAuditApiStatus.errorMessage = '';
      state.updateAuditApiStatus.successMessage = '';
      state.reopenAuditApiStatus.errorMessage = '';
      state.reopenAuditApiStatus.successMessage = '';
      state.searchAuditsApiStatus.errorMessage = '';
      state.searchAuditsApiStatus.successMessage = '';
      state.auditSummaryApiStatus.errorMessage = '';
      state.auditSummaryApiStatus.successMessage = '';
      state.auditStatsApiStatus.errorMessage = '';
      state.auditStatsApiStatus.successMessage = '';
    },
    setCurrentAudit: (state, action: PayloadAction<Audit | undefined>) => {
      state.currentAudit = action?.payload;
    },
    setAuditSummary: (
      state,
      action: PayloadAction<GetAuditSummaryResponse | undefined>,
    ) => {
      state.auditSummary = action?.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAuditsApiThunk.pending, (state) => {
        state.fetchAuditsApiStatus.isLoading = true;
        state.fetchAuditsApiStatus.errorMessage = null;
      })
      .addCase(fetchAuditsApiThunk.fulfilled, (state, action) => {
        state.fetchAuditsApiStatus.isLoading = false;
        state.fetchAuditsApiStatus.isSuccess = true;
        state.fetchAuditsApiStatus.nextToken = action.payload.nextToken;
        state.auditsList = action.payload.audits;
      })
      .addCase(fetchAuditsApiThunk.rejected, (state, action) => {
        state.fetchAuditsApiStatus.isLoading = false;
        state.fetchAuditsApiStatus.isSuccess = false;
        state.fetchAuditsApiStatus.errorMessage = action.payload as string;
      })

      // Create audit cases
      .addCase(createAuditApiThunk.pending, (state) => {
        state.createAuditApiStatus.isLoading = true;
        state.createAuditApiStatus.errorMessage = null;
        state.createAuditApiStatus.successMessage = null;
      })
      .addCase(createAuditApiThunk.fulfilled, (state, action) => {
        state.createAuditApiStatus.isLoading = false;
        state.createAuditApiStatus.isSuccess = true;
        state.createAuditApiStatus.successMessage = action.payload.message;
      })
      .addCase(createAuditApiThunk.rejected, (state, action) => {
        state.createAuditApiStatus.isLoading = false;
        state.createAuditApiStatus.isSuccess = false;
        state.createAuditApiStatus.errorMessage = action.payload as string;
        state.createAuditApiStatus.successMessage = null;
      })

      // Update audit cases
      .addCase(updateAuditApiThunk.pending, (state) => {
        state.updateAuditApiStatus.isLoading = true;
        state.updateAuditApiStatus.errorMessage = null;
        state.updateAuditApiStatus.successMessage = null;
      })
      .addCase(updateAuditApiThunk.fulfilled, (state, action) => {
        state.updateAuditApiStatus.isLoading = false;
        state.updateAuditApiStatus.isSuccess = true;
        state.updateAuditApiStatus.updatedAudit = action.payload.audit;
        auditsSlice.caseReducers.updateAuditInState(state, {
          type: 'updateAuditInState',
          payload: action.payload.audit,
        });
        state.currentAudit = action.payload.audit;
      })
      .addCase(updateAuditApiThunk.rejected, (state, action) => {
        state.updateAuditApiStatus.isLoading = false;
        state.updateAuditApiStatus.isSuccess = false;
        state.updateAuditApiStatus.errorMessage = action.payload as string;
        state.updateAuditApiStatus.successMessage = null;
      })

      // Reopen audit cases
      .addCase(reopenAuditApiThunk.pending, (state) => {
        state.reopenAuditApiStatus.isLoading = true;
        state.reopenAuditApiStatus.errorMessage = null;
        state.reopenAuditApiStatus.successMessage = null;
      })
      .addCase(reopenAuditApiThunk.fulfilled, (state, action) => {
        state.reopenAuditApiStatus.isLoading = false;
        state.reopenAuditApiStatus.isSuccess = true;
        state.reopenAuditApiStatus.successMessage = action.payload.message;
        auditsSlice.caseReducers.updateAuditInState(state, {
          type: 'updateAuditInState',
          payload: action.payload.audit,
        });
      })
      .addCase(reopenAuditApiThunk.rejected, (state, action) => {
        state.reopenAuditApiStatus.isLoading = false;
        state.reopenAuditApiStatus.isSuccess = false;
        state.reopenAuditApiStatus.errorMessage = action.payload as string;
        state.reopenAuditApiStatus.successMessage = null;
      })

      // Search audits cases
      .addCase(searchAuditsApiThunk.pending, (state) => {
        state.searchAuditsApiStatus.isLoading = true;
        state.searchAuditsApiStatus.errorMessage = null;
        state.searchAuditsApiStatus.nextToken = undefined;
      })
      .addCase(searchAuditsApiThunk.fulfilled, (state, action) => {
        state.searchAuditsApiStatus.isLoading = false;
        state.searchAuditsApiStatus.isSuccess = true;
        state.searchAuditsApiStatus.nextToken = action.payload.nextToken;
        state.auditsList = action.payload.audits.filter((item) => item.status);
      })
      .addCase(searchAuditsApiThunk.rejected, (state, action) => {
        state.searchAuditsApiStatus.isLoading = false;
        state.searchAuditsApiStatus.isSuccess = false;
        state.auditsList = [];
        state.searchAuditsApiStatus.errorMessage = action.payload as string;
      })

      // Get audit summary cases
      .addCase(getAuditSummaryApiThunk.pending, (state) => {
        state.auditSummaryApiStatus.isLoading = true;
        state.auditSummaryApiStatus.errorMessage = null;
      })
      .addCase(getAuditSummaryApiThunk.fulfilled, (state, action) => {
        state.auditSummaryApiStatus.isLoading = false;
        state.auditSummaryApiStatus.isSuccess = true;
        state.auditSummary = action.payload;
      })
      .addCase(getAuditSummaryApiThunk.rejected, (state, action) => {
        state.auditSummaryApiStatus.isLoading = false;
        state.auditSummaryApiStatus.isSuccess = false;
        state.auditSummaryApiStatus.errorMessage = action.payload as string;
      })
      // Get audit by Id
      .addCase(getAuditByIdApiThunk.pending, (state) => {
        state.getAuditByIdApiStatus!.isLoading = true;
        state.getAuditByIdApiStatus!.errorMessage = null;
      })
      .addCase(getAuditByIdApiThunk.fulfilled, (state, action) => {
        state.getAuditByIdApiStatus!.isLoading = false;
        state.getAuditByIdApiStatus!.isSuccess = true;
        state.currentAudit = action.payload.audit;
      })
      .addCase(getAuditByIdApiThunk.rejected, (state, action) => {
        state.getAuditByIdApiStatus!.isLoading = false;
        state.getAuditByIdApiStatus!.isSuccess = false;
        state.getAuditByIdApiStatus!.errorMessage = action.payload as string;
      })

      // Get audit stats cases
      .addCase(getAuditStatsApiThunk.pending, (state) => {
        state.auditStatsApiStatus.isLoading = true;
        state.auditStatsApiStatus.errorMessage = null;
      })
      .addCase(getAuditStatsApiThunk.fulfilled, (state, action) => {
        state.auditStatsApiStatus.isLoading = false;
        state.auditStatsApiStatus.isSuccess = true;
        state.auditStats = action.payload;
      })
      .addCase(getAuditStatsApiThunk.rejected, (state, action) => {
        state.auditStatsApiStatus.isLoading = false;
        state.auditStatsApiStatus.isSuccess = false;
        state.auditStatsApiStatus.errorMessage = action.payload as string;
      });
  },
});

export const {
  resetAllApiStatus,
  clearAPIStatusMessages,
  updateAuditInState,
  setCurrentAudit,
  setAuditSummary,
} = auditsSlice.actions;
export default auditsSlice.reducer;
